import moment from 'moment';
import {
    Box,
    Container,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import ButtonBase from '@mui/material/ButtonBase';
import InfiniteScroll from 'react-infinite-scroll-component';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import getTheme from '../../theme/theme';
import { PinnacleThemes } from '../../theme/types';
import { getNotifications, markNotificationAsRead } from '../../api/services/notifications';
import { Notification } from '../../api/interfaces/Notification';
import { handleApiError } from '../../utils/errorHandler';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { SkeletonNotifications } from '../../components/skeleton/SkeletonNotifications/SkeletonNotifications';
import { useNavigate } from 'react-router-dom';
import { watchVideoRedirect } from '../../utils/watchVideoRedirect';
import { profile } from '../../api/services/user';
import { isVideoPlayable } from '../../utils/videoPlayable';
import ReadReferenceDialog from '../../layouts/ReadReferenceDialog/ReadReferenceDialog';
import { getReferenceById } from '../../api/services/references';
import { Reference } from '../../api/interfaces/Reference';
import { SELECT_VIDEO_PLAYER_FULL_BG } from '../../utils/constants';

const NotificationsPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const isMobile = useMediaQuery('(max-width:600px)');
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [notificationsLoading, setNotificationsLoading] = useState(true);
    const [pageNo, setPageNo] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isAccountTrial, setIsAccountTrial] = useState<boolean>(false);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [selectedReference, setSelectedReference] = useState<Reference | null>(null);
    const [loadingReference, setLoadingReference] = useState<boolean>(true);
    const [referenceThumbnail, setReferenceThumbnail] = useState<string>('');

    const fetchNotifications = useCallback(async (pageNo = 1) => {
        setNotificationsLoading(true);
        try {
            const notificationsResponse = await getNotifications({ page: pageNo });

            const newNotifications = notificationsResponse.data.data;

            setNotifications((prev) => [...prev, ...newNotifications]);

            if (pageNo >= notificationsResponse.data.totalPages) {
                setHasMore(false);
            }

        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setNotificationsLoading(false);
        }
    }, []);

    const loadMore = () => {
        const nextPage = pageNo + 1;
        setPageNo(nextPage);
        fetchNotifications(nextPage);
    };

    useEffect(() => {
        const init = async () => {
            try {
                const userResponse = await profile();
                const userProfile = userResponse.data.user;
                setIsAccountTrial(userProfile.isTrial ?? false);

                // Reset states on first load
                setPageNo(1);
                setNotifications([]);
                setHasMore(true);

                await fetchNotifications(1);
            } catch (e) {
                console.error(e);
            }
        };
        init();
    }, [fetchNotifications]);

    const handleOpenNotification = async (notification: Notification) => {
        const { _id, isRead, isTrial, resourceId, type, resourceDetails } = notification;

        // Handle navigate based on notification type
        if (type === "VIDEO") {
            const playable = isVideoPlayable(isAccountTrial ?? false, true, (resourceDetails.videoUrl ?? '') || (resourceDetails.videoUrl1 ?? '') || (resourceDetails.videoUrl2 ?? ''));
            const video = {
                _id: resourceId,
                videoUrl: resourceDetails.videoUrl,
                videoUrl1: resourceDetails.videoUrl1,
                videoUrl2: resourceDetails.videoUrl2,
                isTrial: isTrial
            };
            watchVideoRedirect(navigate, video, playable)
        } else if (type === "REFERENCE") {
            setLoadingReference(true);
            setSelectedReference(null);
            setReferenceThumbnail(notification.thumbnail);
            setDialogOpen(true);
            try {
                const referenceResponse = await getReferenceById(resourceId);
                setSelectedReference(referenceResponse.data);
            } catch (error) {
                setDialogOpen(false);
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({ details: msg, type: NotificationTypes.Error });
                    },
                });
            } finally {
                setLoadingReference(false);
            }
        }

        // Mark notification as read in background
        if (!isRead) {
            markNotificationAsRead(_id);
        }
        setNotifications((prev) =>
            prev.map((notif) =>
                notif._id === _id ? { ...notif, isRead: true } : notif
            )
        );
    };

    return (
        <>
            <main
                style={{
                    display: 'flex',
                    width: '100%',
                    minHeight: '100vh',
                    backgroundColor: 'rgba(0, 8, 15, 1)',
                    backgroundImage: `url(${SELECT_VIDEO_PLAYER_FULL_BG})`,
                    backgroundSize: 'cover',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                }}
            >
                {/* Sidebar always outside */}
                <Sidebar />

                <Box
                    component="section"
                    sx={{
                        flexGrow: 1,
                        width: '100%',
                        ml: !isMobile ? '72px' : 0,
                        overflowX: 'hidden',
                        mt: !isMobile ? 0 : 7
                    }}
                >
                    <Container
                        maxWidth="md"
                        sx={{
                            mt: { xs: 4, sm: 6 },
                            mb: 4,
                            px: { xs: 2, sm: 3 },
                        }}
                    >
                        <Box sx={{ width: '100%' }}>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 600,
                                    mb: 4,
                                }}
                            >
                                Notifications
                            </Typography>

                            <InfiniteScroll
                                dataLength={notifications.length}
                                next={loadMore}
                                hasMore={hasMore}
                                loader={
                                    notificationsLoading && (
                                        <Stack spacing={2}>
                                            {Array.from({ length: 5 }).map((_, idx) => (
                                                <SkeletonNotifications key={idx} />
                                            ))}
                                        </Stack>
                                    )
                                }
                                scrollThreshold={0.9}
                                style={{ overflow: 'visible' }}
                            >
                                <Stack spacing={2}>
                                    {notifications.map((notif, index) => (
                                        <ButtonBase
                                            key={`${notif._id}-${index}`}
                                            onClick={() => handleOpenNotification(notif)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                borderRadius: 2,
                                                px: 2,
                                                py: 1,
                                                width: '100%',
                                                textAlign: 'left',
                                                transition: 'background 0.2s',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    background: notif.isRead ? 'transparent' : 'rgba(255, 255, 255, 0.03)',
                                                },
                                                '&:focus-visible': {
                                                    outline: 'none',
                                                },
                                            }}
                                        >
                                            {/* Bullet */}
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    backgroundColor: notif.isRead ? '#888' : '#FFD600',
                                                    flexShrink: 0,
                                                    mr: 2,
                                                }}
                                            />
                                            {/* Thumbnail or fallback */}
                                            <Box
                                                component="img"
                                                loading="lazy"
                                                src={notif.thumbnail}
                                                alt={notif.description}
                                                sx={{
                                                    width: 100,
                                                    height: 100,
                                                    flexShrink: 0,
                                                    mr: 2,
                                                    color: theme.palette.common.white,
                                                    filter: notif.isRead ? 'brightness(0.6)' : undefined,
                                                    borderRadius: '12px',
                                                    objectFit: notif.type === "REFERENCE" ? 'contain' : 'none',
                                                    border: notif.type === "REFERENCE" ? '1px solid rgba(55, 55, 55, 1)' : undefined,

                                                }}
                                            />

                                            {/* Text content */}
                                            <Box
                                                flex={1}
                                                display="flex"
                                                flexDirection="column"
                                                justifyContent="space-between"
                                                minWidth={0}
                                            >
                                                {/* Top group */}
                                                <Box sx={{ mb: 3 }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 500,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            color: notif.isRead ? '#888' : 'var(--secondary-c)',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        {notif.description}
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: notif.isRead ? '#888' : theme.palette.common.white,
                                                        }}
                                                    >
                                                        {notif.type === "REFERENCE" ? 'reference' : notif.resourceDetails.duration}
                                                    </Typography>

                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: notif.isRead ? '#888' : theme.palette.common.white,
                                                        }}
                                                    >
                                                        {notif.duration}
                                                    </Typography>
                                                </Box>

                                                {/* Bottom */}
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: notif.isRead ? '#888' : theme.palette.common.white,
                                                    }}
                                                >
                                                    {moment().diff(moment(notif.createdAt), 'hours') < 24
                                                        ? moment(notif.createdAt).fromNow() // e.g., "2 hours ago"
                                                        : moment(notif.createdAt).format('MMMM DD, YYYY')} {/* e.g., "June 07, 2025" */}
                                                </Typography>
                                            </Box>
                                        </ButtonBase>
                                    ))}
                                </Stack>

                            </InfiniteScroll>
                        </Box>
                    </Container>
                </Box>
            </main>

            {/* REFERENCE DIALOG BOX */}
            <ReadReferenceDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                isLoading={loadingReference}
                reference={selectedReference}
                onFavoriteToggle={(id, isFav) => {
                    if (selectedReference && selectedReference._id === id) {
                        setSelectedReference({ ...selectedReference, isFavorite: isFav });
                    }
                }}
                referenceThumbnail={referenceThumbnail}
            />
        </>
    );
};

export default NotificationsPage;
